/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

/**
 * Message unique porté par tous les champs mis en erreur : la règle est
 * « au moins un des deux », elle ne vise aucun représentant en particulier.
 *
 * Le libellé parle de « représentants légaux » et non de « parents » car la
 * règle ne se déclenche que si `protectionJuridique === true`, cas dans lequel
 * `hasRepresentantsLegaux` est vrai et où l'étape affiche déjà
 * « Représentant légal 1 » / « Représentant légal 2 ».
 */
export const MESSAGE_REPRESENTANT_LEGAL_REQUIS =
    'Au moins un des deux représentants légaux doit être renseigné (nom et prénom)';

/**
 * Un représentant est exploitable si son nom ET son prénom sont saisis.
 * `_.trim` absorbe en une passe `null`, `undefined`, `''` et les espaces seuls
 * (l'UI écrit `''` quand on vide un champ, cf. useNomFamilleField).
 */
const estIdentifie = (parent: any) =>
    !_.isEmpty(_.trim(parent?.nomFamille)) && !_.isEmpty(_.trim(parent?.prenom));

/**
 * Ne signale que les champs d'identité réellement manquants du représentant :
 * inutile de marquer en rouge un nom déjà saisi.
 */
const champsIdentiteManquants = (parent: any) => {
    const errors = {
        nomFamille: '',
        prenom: '',
    };

    if (_.isEmpty(_.trim(parent?.nomFamille))) {
        errors.nomFamille = MESSAGE_REPRESENTANT_LEGAL_REQUIS;
    }

    if (_.isEmpty(_.trim(parent?.prenom))) {
        errors.prenom = MESSAGE_REPRESENTANT_LEGAL_REQUIS;
    }

    return _.pickBy(errors, (value) => value !== '');
};

/**
 * Le libellé de la commune déléguée de naissance est obligatoire dès que la
 * case est cochée. Contrairement à `champsIdentiteManquants`, cette règle
 * s'applique toujours : le champ est affiché pour chaque parent/représentant,
 * indépendamment de `protectionJuridique`.
 */
const communeDelegueManquante = (parent: any) => {
    const errors = { naissanceLibelleCommuneDelegue: '' };

    if (
        parent?.adresseNaissance?.naissanceCommuneDelegue
        && _.isEmpty(_.trim(parent?.adresseNaissance?.naissanceLibelleCommuneDelegue))
    ) {
        errors.naissanceLibelleCommuneDelegue = 'Le libellé de la commune déléguée de naissance est obligatoire';
    }

    return _.pickBy(errors, (value) => value !== '');
};

/**
 * Lorsqu'un partenaire est sous mesure de protection juridique, ses « parents »
 * sont ses représentants légaux : au moins l'un des deux doit être identifié
 * (nom de famille + prénom).
 *
 * Les clés retournées correspondent aux deux colonnes de `StepParentsPartenaire` :
 * `parent1` = `perePartenaire{N}`, `parent2` = `merePartenaire{N}`.
 *
 * Règle volontairement limitée à `protectionJuridique` : `protectionOfpra`
 * relabellise l'étape (cf. `hasRepresentantsLegaux`) mais n'impose pas de
 * représentant légal.
 *
 * `filiationInconnue{Mere,Pere}Partenaire{N}` n'est pas atteignable en PACS
 * (la case est conditionnée à `isNotPACS` dans EtatCivil) : pas de
 * court-circuit ici, contrairement à `validationInformationsNaissance`.
 */
const validationParentsPartenaire = (
    data: any,
    numeroPartenaire: number,
) => {
    const conventionDeclaration = data?.conventionDeclaration;
    const partenaire = conventionDeclaration?.[`partenaire${numeroPartenaire}`];
    const parent1 = conventionDeclaration?.[`perePartenaire${numeroPartenaire}`];
    const parent2 = conventionDeclaration?.[`merePartenaire${numeroPartenaire}`];

    // Commune déléguée de naissance : s'applique toujours, que le partenaire
    // soit ou non sous protection juridique (le champ est affiché dans les
    // deux cas).
    const errorsParent1 = communeDelegueManquante(parent1);
    const errorsParent2 = communeDelegueManquante(parent2);

    // Identité des représentants légaux : uniquement si le partenaire est
    // sous mesure de protection juridique et qu'aucun des deux représentants
    // n'est identifié. Le `!== true` couvre `false`, `null` (valeur par
    // défaut), `undefined` (partenaire reconstruit par `mapConventionToPacs`)
    // et un partenaire absent.
    if (
        partenaire?.protectionJuridique === true
        && !estIdentifie(parent1)
        && !estIdentifie(parent2)
    ) {
        Object.assign(errorsParent1, champsIdentiteManquants(parent1));
        Object.assign(errorsParent2, champsIdentiteManquants(parent2));
    }

    return _.pickBy(
        { parent1: errorsParent1, parent2: errorsParent2 },
        (value) => !_.isEmpty(value),
    );
};

export default validationParentsPartenaire;
